import React, { Component } from 'react';

import MediaQuery from 'react-responsive';
import { connect } from 'react-redux';
import { submit, isSubmitting } from 'redux-form';

import {
  StyledHeader,
  StyledMain,
  StyledHeaderContainer,
  StyledAddButton,
  StyledTableContainer,
} from 'helpers/StyledTableComponentsHelper';

import {
  getProperties,
  getUnits,
  getCurrentLeasesOfUnit,
  showCurrentLeasesOfUnit,
  showUnits,
} from 'actions/landlordPropertiesActions';

import { AccentColor } from 'helpers/AccentColorHelper';

import HeaderWithPeriod from 'components/shared/HeaderWithPeriod';

import { isFilterInStrings } from 'helpers/StringHelper.js';
import { toFriendlyDateString, formatMoney } from 'helpers/Formatter';

import withMobileDialog from '@material-ui/core/withMobileDialog';

import * as landlordPaymentsActions from 'actions/landlordPaymentsActions';

import TableFilters from 'components/ui/TableFilters';
import TabbedTable from 'components/ui/TabbedTable';
import MobileTable from 'components/ui/MobileTable';
import StatusTag from 'components/ui/StatusTag';

import { default as AntDrawer } from 'components/ui/Drawer';

import PaymentInfoDialog from './components/PaymentInfoDialog';
import CreatePaymentDrawerWizard from './CreatePaymentDrawerWizard';

import { getTranslatedString } from 'helpers/i18n';

export default withMobileDialog()(
  connect(
    store => ({
      fetchingPayments: store.landlordPayments.fetchingPayments,
      payments: store.landlordPayments.payments,
      units: store.landlordProperties.units,
      properties: store.landlordProperties.properties,
      submitting: isSubmitting('manual_payment_creator')(store),
      paymentsCount: store.landlordPayments.paymentsCount,
      currentLeasesOfUnit: store.landlordProperties.currentLeasesOfUnit,
    }),
    {
      ...landlordPaymentsActions,
      getUnits,
      getProperties,
      submit,
      getCurrentLeasesOfUnit,
      showCurrentLeasesOfUnit,
      showUnits,
    }
  )(
    class Payments extends Component {
      constructor(props) {
        super(props);
        this.state = {
          showFilters: false,
          filtersActive: false,
          filters: {
            search: '',
            properties: Array(this.props.properties.length).fill(false),
          },
          currentTab: 'outstanding',
          sortKey: '',
          sortOrder: '',
          offset: 0,
          drawerIsOpen: false,
        };
      }

      setRef = ref => (this.table = ref);

      fetchPaymentsAndStats = () => {
        this.props.getPaymentStats();
        this.fetchPayments();
      };

      fetchPayments = () => {
        const { filters, filtersActive, currentTab, offset, sortKey, sortOrder } = this.state;

        let fetchPromise = null;

        let filterOptions = {
          sort_key: sortKey,
          sort_order: sortOrder,
        };

        if (filtersActive) {
          const propertiesToFilter = this.filterProperties(filters.properties).map(
            data => data.value
          );

          filterOptions = {
            query: filters.search,
            property_ids: propertiesToFilter,
            sort_key: sortKey,
            sort_order: sortOrder,
          };

          fetchPromise = this.props.getFilteredPayments('all', offset, filterOptions);
        } else {
          fetchPromise = this.props.getFilteredPayments(currentTab, offset, filterOptions);
        }

        fetchPromise.then(() => {
          this.setState({
            offset: this.props.payments.length,
          });
        });
      };

      componentDidMount = () => {
        this.fetchPaymentsAndStats();
        this.props.getProperties({ for_multiselect: true, show_cities: true });
      };

      filterProperties = booleanArrayMask => {
        return this.props.properties.filter((_, i) => booleanArrayMask[i]);
      };

      filterFunctions = {
        search: valuesToFilterWith => item =>
          valuesToFilterWith.length === 0 ||
          isFilterInStrings(this.columns().map(column => item[column.dataKey]), valuesToFilterWith),
        properties: valuesToFilterWith => item => {
          const propertiesToFilter = this.filterProperties(valuesToFilterWith).map(
            data => data.label
          );
          if (propertiesToFilter.length === 0) return true;
          return propertiesToFilter
            .map(property => property.toLowerCase())
            .includes(item.property_name.toLowerCase());
        },
      };

      handleClearSearch = setFieldValue => {
        this.setState(
          {
            filtersActive: false,
            filters: {
              search: '',
              properties: Array(this.props.properties.length).fill(false),
            },
            offset: 0,
          },
          () => this.fetchPayments()
        );

        if (this.table) this.table.scrollToTop(0);
        setFieldValue('search', '');
        setFieldValue('properties', Array(this.props.properties.length).fill(false));
      };

      handleFilters = values => {
        const isFiltersUntouched = values.search === '' && !values.properties.includes(true);
        const newState = {
          filtersActive: !isFiltersUntouched,
          filters: {
            search: values.search,
            properties: values.properties,
          },
          drawerVisible: false,
          offset: 0,
        };

        if (this.table) this.table.scrollToTop(0);
        this.setState(newState, () => this.fetchPayments());
      };

      handleSort = (key, order) => {
        this.setState(
          {
            sortKey: key,
            sortOrder: order,
            offset: 0,
          },
          () => {
            this.fetchPayments();
          }
        );
        if (this.table) this.table.scrollToTop(0);
      };

      columns = () => {
        return [
          {
            title: getTranslatedString('payments.status'),
            dataKey: 'status',
            sortable: false,
            width: 150,
            render: ({ data }) => <StatusTag status={data} />,
          },
          {
            title: getTranslatedString('payments.paymentType'),
            dataKey: 'payment_type',
            sortable: true,
            width: 175,
          },
          {
            title: getTranslatedString('payments.unitNumber'),
            dataKey: 'unit_number',
            sortable: true,
            width: 125,
          },
          {
            title: getTranslatedString('payments.dueDate'),
            dataKey: 'due_date',
            sortable: true,
            width: 175,
            render: ({ data }) => (data ? toFriendlyDateString(data) : ''),
          },
          {
            title: getTranslatedString('payments.tenants'),
            dataKey: 'cotenant_full_names',
            sortable: true,
            width: 300,
          },
          {
            title: getTranslatedString('payments.amountDue'),
            dataKey: 'amount',
            sortable: true,
            width: 175,
            render: ({ data }) => (data ? formatMoney(data) : ''),
          },
          {
            title: getTranslatedString('payments.amountOutstanding'),
            dataKey: 'amount_outstanding',
            sortable: true,
            width: 175,
            render: ({ data }) => formatMoney(data),
          },
        ];
      };

      handleTabChange = tabName => {
        this.setState(
          {
            currentTab: tabName,
            offset: 0,
          },
          () => {
            this.fetchPayments();
          }
        );
        if (this.table) this.table.scrollToTop(0);
      };

      onSuccessChange = () => {
        this.setState(
          {
            offset: 0,
          },
          () => {
            this.fetchPaymentsAndStats();
          }
        );
        if (this.table) this.table.scrollToTop(0);
      };

      accentColor = AccentColor();

      filterOptions = [
        {
          useSearch: true,
          options: this.props.properties.map(data => data.label),
          name: 'properties',
          title: 'Select properties to filter by',
        },
      ];

      submitPayment = () => {
        this.props.submit('manual_payment_creator');
      };

      toggleDialog = () => {
        this.setState(prevState => ({
          createPaymentDrawerOpen: !prevState.createPaymentDrawerOpen,
        }));
      };

      submitForm = formData => {
        return this.props.createPayment(formData).then(() => {
          this.toggleDialog();
          this.fetchPayments();
        });
      };

      toggleDrawer = paymentID => {
        if (paymentID) {
          this.props.openPaymentDialog(paymentID).then(() => {
            this.props.getTenantList(paymentID);
            this.setState({ showDrawer: true });
          });
          return;
        }

        this.setState({ showDrawer: false });
      };

      listUnits = async value => {
        // Clear units and leases on reselection
        await this.props.showUnits([]);
        await this.props.showCurrentLeasesOfUnit([]);

        return this.props.getUnits(value, { for_multiselect: true });
      };

      fetchLeases = async unitId => {
        // Clear units on reselection
        await this.props.showCurrentLeasesOfUnit([]);
        this.props.getCurrentLeasesOfUnit(unitId, null, { activeLeasesOnly: true });
      };

      render() {
        const { filters } = this.state;
        const { payments, paymentsCount } = this.props;

        const filterOptions = [
          {
            useSearch: true,
            options: this.props.properties.map(data => data.label),
            name: 'properties',
            title: 'Select properties to filter by',
          },
        ];

        const tabs = [
          {
            title: getTranslatedString('payments.outstanding'),
            count: paymentsCount['outstanding'] || 0,
            onClick: () => this.handleTabChange('outstanding', { subset: 'outstanding' }),
          },
          {
            title: getTranslatedString('payments.late'),
            count: paymentsCount['late'] || 0,
            onClick: () => this.handleTabChange('late', { subset: 'late' }),
          },
          {
            title: getTranslatedString('payments.NSF'),
            count: paymentsCount['nsf'] || 0,
            onClick: () => this.handleTabChange('nsf', { subset: 'nsf' }),
          },
          {
            title: getTranslatedString('payments.paid'),
            count: paymentsCount['paid'] || 0,
            onClick: () => this.handleTabChange('paid', { subset: 'paid' }),
          },
          {
            title: getTranslatedString('payments.deposit'),
            count: paymentsCount['deposit'] || 0,
            onClick: () => this.handleTabChange('deposit', { subset: 'deposit' }),
          },
          {
            title: getTranslatedString('payments.all'),
            count: paymentsCount['all'] || 0,
            onClick: () => this.handleTabChange('all', { subset: 'all' }),
          },
        ];

        return (
          <StyledMain>
            <StyledHeaderContainer>
              <StyledHeader>
                <HeaderWithPeriod
                  headerText={<span>{getTranslatedString('landlordPortal.charges.charges')}</span>}
                  newStyle
                />
                <StyledAddButton accentColor={this.accentColor} onClick={this.toggleDialog}>
                  {getTranslatedString('landlordPortal.charges.addLeaseCharge')}
                </StyledAddButton>
              </StyledHeader>
              <TableFilters
                filters={filters}
                handleSubmitFilters={this.handleFilters}
                handleClearFilters={this.handleClearSearch}
                filterOptions={filterOptions}
                accentColor={this.accentColor}
              />
            </StyledHeaderContainer>

            {this.renderPaymentOptions()}

            <StyledTableContainer>
              {this._renderTableView({ payments, tabs })}
              {this._renderMobileView({ payments })}
            </StyledTableContainer>

            <AntDrawer
              maskClosable={false}
              keyboard
              visible={this.state.createPaymentDrawerOpen}
              onClose={this.toggleDialog}
              closable={false}
              width={600}
            >
              {this.state.createPaymentDrawerOpen && (
                <CreatePaymentDrawerWizard
                  currentLeasesOfUnit={this.props.currentLeasesOfUnit}
                  units={this.props.units}
                  listUnits={this.listUnits}
                  handleClose={this.toggleDialog}
                  fetchLeases={this.fetchLeases}
                  properties={this.props.properties}
                  onSuccess={this.onSuccessChange}
                />
              )}
            </AntDrawer>
          </StyledMain>
        );
      }

      renderPaymentOptions = () => (
        <PaymentInfoDialog
          isOpen={this.state.showDrawer}
          toggleDialog={this.toggleDrawer}
          getAllPayments={this.onSuccessChange}
        />
      );

      _renderMobileView = ({ payments }) => (
        <MediaQuery query="(max-device-width: 750px)">
          <MobileTable
            groupRowsBy="property_name"
            data={payments}
            columns={this.columns()}
            title="due_date"
            tag="status"
            stickyHeaderTop={this.state.filters.search ? '137px' : '107px'}
            fieldsAboveFold={[
              {
                title: 'Unit Number',
                key: 'unit_number',
              },
              {
                title: 'Amount Due',
                key: 'amount',
                dataType: 'money',
              },
            ]}
            fieldsBelowFold={[
              {
                title: 'Residents',
                key: 'cotenant_full_names',
              },
              {
                title: 'Due Date',
                key: 'due_date',
              },
              {
                title: 'Payment Type',
                key: 'payment_type',
              },
              {
                title: 'Amount Outstanding',
                key: 'amount_outstanding',
                dataType: 'money',
              },
            ]}
            onRowClick={data => {
              this.toggleDrawer(data.id);
            }}
          />
        </MediaQuery>
      );

      _renderTableView = ({ payments, tabs }) => (
        <MediaQuery query="(min-device-width: 750px)">
          <TabbedTable
            setRef={this.setRef}
            groupRowsBy="property_name"
            tabs={tabs}
            columns={this.columns()}
            data={payments}
            loadMoreData={this.fetchPayments}
            isLoading={this.props.fetchingPayments}
            filtersActive={this.state.filtersActive}
            onRowClick={data => {
              this.toggleDrawer(data.original_id);
            }}
            onColumnClick={data => {
              this.handleSort(data.key, data.order);
            }}
          />
        </MediaQuery>
      );
    }
  )
);
